<template>
  <div
    class="opc-drawer"
    :class="{
      'opc-drawer--open': is,
      'opc-modal': isModal,
    }"
    :style="is ? { height: this.height } : {}"
  >
    <div
      class="opc-drawer__inner"
      :class="{
        'opc-modal': isModal,
      }"
    >
      <div
        class="opc-drawer__head"
        @touchstart.stop
        :class="{
          'opc-modal': isModal,
        }"
      >
        <zm-button
          type="link"
          ref="opc-drawer-control"
          @click="toggle"
          :aria-expanded="is ? 'true' : 'false'"
          :suffix-icon="suffixIcon"
        >
          {{ label }}
        </zm-button>
        <div
          class="opc-drawer__head__slot"
          :class="{
            'opc-modal': isModal,
          }"
        >
          <slot name="head" />
        </div>
      </div>
      <div
        class="opc-drawer__body"
        :class="{
          'opc-modal': isModal,
        }"
        ref="mx_ariaBody"
        role="dialog"
        aria-modal="true"
        :style="{ display: is ? 'block' : 'none' }"
        :aria-hidden="is ? 'false' : 'true'"
        @touchstart.stop
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ariaDialogMixin from '@/common/mixin/aria-dialog-mixin';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';

export default {
  mixins: [ariaDialogMixin],
  props: {
    label: {
      type: String,
      default: 'Payment Details',
    },
    height: {
      type: String,
      default: '60vh',
    },
    scrollToTop: {
      type: Boolean,
      default: false,
    },
    isModal: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      is: false,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
    ]),
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.$context.type).get(0)
    },
    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
    suffixIcon() {
      if (this.isModal) {
        return this.is ? 'zm-icon-down' : 'zm-icon-up';
      } else {
        return this.is ? 'zm-icon-up' : 'zm-icon-down';
      }
    }
  },
  watch: {
    is(val) {
      if (this.$context.is.modal) {
        this.$tracking.buyInteraction(`order summary ${this.is ? 'expand' : 'collapse'}`, this.planTrackingName);
      } else {
        this.$tracking.buyInteraction('mobile payment details accordion', this.is ? 'expand' : 'collapse');
      }
      this.mx_ariaDialogOpened = this.is;

      if (val && this.scrollToTop) {
        this.handlerScrollToTop();
      }
    },
  },
  methods: {
    toggle() {
      this.is = !this.is;
    },
    onTouchOutside() {
      this.is = false;
    },
    onKeyDown(event) {
      if (event.keyCode === 27) {
        this.is = false;
      }
    },
    handlerScrollToTop() {
      requestAnimationFrame?.(() => {
        this.$refs.mx_ariaBody?.scrollTo?.({ top: 0 });
      });
    },
  },
  mounted() {
    window.document.addEventListener('touchstart', this.onTouchOutside);
    window.document.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    window.document.removeEventListener('touchstart', this.onTouchOutside);
    window.document.removeEventListener('keydown', this.onKeyDown);
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

$head-height: 50px;
.opc-drawer {
  height: $head-height;
  transition: all 0.3s ease-in-out;

  &.opc-modal {
    height: 85vh;
    &.opc-drawer--open .opc-drawer__body {
      animation: slide-in-bottom 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
    }
  }

  &__inner {
    height: 100%;
    @include media(tablet, desktop) {
      max-width: $tablet-inner-width;
      margin: 0 auto;
    }
    &.opc-modal {
      position: relative;
      max-width: 100%;
    }
  }

  &__head {
    padding: 15px 24px;
    height: $head-height - 1px;
    display: flex;
    flex-direction: row;
    justify-content: left;

    &.opc-modal {
      width: 100%;
      height: 78px;
      position: absolute;
      background: #f7f7f8;
      z-index: 2;
      cursor: pointer;
      @include media(mobile) {
        padding: 12px;
      }
    }

    &__slot {
      margin-left: auto;
      &.opc-modal {
        display: flex;
        align-items: center;
      }
    }

    &__trigger {
      padding: 0px 8px;
      position: relative;
      left: -8px;
      border-radius: 10px;
    }

    &--down &__trigger {
      background: #f2f8ff;
    }
  }

  &__body {
    overflow: auto;
    max-height: calc(100% - #{$head-height});
    background: white;
    // border-radius: 0 0 24px 24px;
    box-shadow: 0px 10px 16px 0px rgba(0, 0, 0, 0.12);

    &.opc-modal {
      background: none;
      bottom: 100%;
      left: 0;
      right: 0;
      position: absolute;
      z-index: 1;
      box-shadow: none !important;
      animation: slide-out-bottom 0.5s cubic-bezier(0.55, 0.085, 0.68, 0.53) both;
      height: calc(100% - 76px);
      &::-webkit-scrollbar {
        width: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.42);
        border-radius: 2px;

        @include darkTheme() {
          background: #babacc;
        }
      }
    }
  }
}

@keyframes slide-in-bottom {
  0% {
    transform: translateY(1000px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slide-out-bottom {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(1000px);
    opacity: 0;
  }
}
</style>
